import type { AppointmentsPageData, PastAppointment, Provider, UpcomingAppointment } from "../types"

/**
 * Mock fixtures, one per frame of the S4 design, so every state of the Appointments page is
 * reachable while the API is still being built. Nothing here ships to a member.
 */

const MINUTE_MS = 60 * 1000
const DAY_MS = 24 * 60 * 60 * 1000

/**
 * An instant offset from *now*, never a frozen literal, so "in 3 days" and "in 5 mins" stay true
 * whenever the page is opened. Offsets from `Date.now()` are zone-independent by construction,
 * which is what lets the same fixture render as a different local time in a different zone.
 */
const minutesFromNow = (minutes: number): Date => new Date(Date.now() + minutes * MINUTE_MS)
const daysFromNow = (days: number): Date => new Date(Date.now() + days * DAY_MS)

/** A date-only `YYYY-MM-DD` string, N days back. Derived from the UTC calendar, so it never shifts. */
const daysAgoDateOnly = (days: number): string => new Date(Date.now() - days * DAY_MS).toISOString().slice(0, 10)

// Photos are deliberately absent: the avatar falls back to initials rather than shipping image files.
// Honorifics follow the design's own rows verbatim — frame 5 draws "Jane Doe, MD" with no "Dr." while
// every other row has one, which is why the honorific is a field rather than a prefix on every name.
const susannaPark: Provider = { honorific: "Dr.", name: "Susanna Park", title: "MD", photoUrl: null }
const janeDoe: Provider = { name: "Jane Doe", title: "MD", photoUrl: null }
const priyaRaman: Provider = { honorific: "Dr.", name: "Priya Raman", title: "MD", photoUrl: null }
const alanOkafor: Provider = { honorific: "Dr.", name: "Alan Okafor", title: "MD", photoUrl: null }

const specialists: Provider[] = [priyaRaman, alanOkafor, susannaPark]

const MOCK_JOIN_URL = "https://carrot.zoom.us/j/00000000000"

const upcomingIn = (
  date: Date,
  formsComplete: boolean,
  overrides: { id?: string; provider?: Provider } = {}
): UpcomingAppointment => ({
  id: overrides.id ?? "upcoming-1",
  provider: overrides.provider ?? susannaPark,
  appointmentType: "initialConsult",
  date,
  formsComplete,
  joinUrl: MOCK_JOIN_URL,
  status: "scheduled"
})

/**
 * Four rows, not two: the design keeps the first two visible and hides the rest behind the chevron,
 * so there has to be something left to reveal.
 */
const pastVisits = (): PastAppointment[] => [
  {
    id: "past-1",
    provider: susannaPark,
    appointmentType: "initialConsult",
    date: daysAgoDateOnly(75),
    status: "completed"
  },
  {
    id: "past-2",
    provider: janeDoe,
    appointmentType: "initialConsult",
    date: daysAgoDateOnly(90),
    status: "completed"
  },
  {
    id: "past-3",
    provider: priyaRaman,
    appointmentType: "followUpConsult",
    date: daysAgoDateOnly(126),
    status: "completed"
  },
  {
    id: "past-4",
    provider: alanOkafor,
    appointmentType: "initialConsult",
    date: daysAgoDateOnly(170),
    status: "completed"
  }
]

const scenarioBuilders = {
  /** Frame 1 — eligible, nothing booked, nothing behind them. */
  empty: (): AppointmentsPageData => ({ eligible: true, upcoming: [], past: [], specialists }),

  /** Frame 2 — booked with forms outstanding, no history, so the Past section has nothing to show. */
  upcomingNoPast: (): AppointmentsPageData => ({
    eligible: true,
    upcoming: [upcomingIn(daysFromNow(3), false)],
    past: [],
    specialists
  }),

  /** Frame 3 — forms already done, so Cancel is the only action left. */
  upcomingFormsComplete: (): AppointmentsPageData => ({
    eligible: true,
    upcoming: [upcomingIn(daysFromNow(3), true)],
    past: [],
    specialists
  }),

  /**
   * Frame 4 — imminent. Five minutes sits well inside the 30-minute threshold the Up next card
   * uses, and forms stay outstanding so that Join is visibly the imminent branch winning rather
   * than the forms-complete branch.
   */
  upcomingToday: (): AppointmentsPageData => ({
    eligible: true,
    upcoming: [upcomingIn(minutesFromNow(5), false)],
    past: [],
    specialists
  }),

  /** Frame 5 — the fullest page: Up next, then Past, then the booking card. */
  upcomingWithHistory: (): AppointmentsPageData => ({
    eligible: true,
    upcoming: [upcomingIn(daysFromNow(3), false)],
    past: pastVisits(),
    specialists
  }),

  /**
   * Two visits booked at once — the state MEM-848 added, and the only scenario here with no S4
   * frame behind it: the design for it landed separately (Britt, 9/4), so this fixture follows the
   * requirement rather than a frame.
   *
   * Distinct providers and opposite `formsComplete` values, deliberately: it makes the per-card
   * actions visibly independent, which is the thing most likely to break when one card becomes many.
   */
  upcomingMultiple: (): AppointmentsPageData => ({
    eligible: true,
    upcoming: [
      upcomingIn(daysFromNow(3), false),
      upcomingIn(daysFromNow(17), true, { id: "upcoming-2", provider: priyaRaman })
    ],
    past: pastVisits(),
    specialists
  }),

  /** Frame 8 — nothing booked, so the booking card leads and Past sits below it. */
  noUpcomingWithHistory: (): AppointmentsPageData => ({
    eligible: true,
    upcoming: [],
    past: pastVisits(),
    specialists
  }),

  /**
   * Frame 10 — cannot book. Drawn with no Past section at all, which is a provisional default:
   * design has not confirmed whether a non-eligible member with history should see it.
   */
  notEligible: (): AppointmentsPageData => ({ eligible: false, upcoming: [], past: [], specialists })
}

export type AppointmentScenario = keyof typeof scenarioBuilders

export const APPOINTMENT_SCENARIOS = Object.keys(scenarioBuilders) as AppointmentScenario[]

/** Preserves what the page rendered before the mock landed: an upcoming visit plus history. */
export const DEFAULT_APPOINTMENT_SCENARIO: AppointmentScenario = "upcomingWithHistory"

export const isAppointmentScenario = (value: string | null | undefined): value is AppointmentScenario =>
  value != null && Object.prototype.hasOwnProperty.call(scenarioBuilders, value)

/**
 * Built fresh on every call so the relative dates inside are current, never frozen at import time.
 *
 * Dispatched with an explicit `switch` rather than `scenarioBuilders[scenario]()` or a `Map` lookup.
 * `scenario` originates in a URL query param, and CodeQL's `js/unvalidated-dynamic-method-call`
 * fires on *calling a function value that was selected by user-controlled input* — the taint flows
 * through `Map.get()` just as it does through a computed member access, so neither avoids it. Only
 * static call sites break the flow. The `never` assignment below makes the compiler reject a new
 * scenario that is added to `scenarioBuilders` without a case here.
 */
export const getScenarioData = (scenario: AppointmentScenario): AppointmentsPageData => {
  switch (scenario) {
    case "empty":
      return scenarioBuilders.empty()
    case "upcomingNoPast":
      return scenarioBuilders.upcomingNoPast()
    case "upcomingFormsComplete":
      return scenarioBuilders.upcomingFormsComplete()
    case "upcomingToday":
      return scenarioBuilders.upcomingToday()
    case "upcomingWithHistory":
      return scenarioBuilders.upcomingWithHistory()
    case "upcomingMultiple":
      return scenarioBuilders.upcomingMultiple()
    case "noUpcomingWithHistory":
      return scenarioBuilders.noUpcomingWithHistory()
    case "notEligible":
      return scenarioBuilders.notEligible()
    default: {
      const unhandled: never = scenario
      throw new Error(`Unhandled appointment scenario: ${String(unhandled)}`)
    }
  }
}
